import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
} from '@angular/core';
import { IccAppearance } from '../../enums/appearance';
import { InteractiveState } from '../../enums/interactive-state';
import { IconComponent } from '../icon/icon.component';
import { TinyColor } from '@ctrl/tinycolor';

@Component({
    selector: 'icc-chip',
    templateUrl: './chip.component.html',
    styleUrls: ['./chip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipComponent implements AfterViewInit {
    hovered = false;
    pressed = false;
    focusVisible = false;
    focused = false;

    @Input()
    pseudoHovered: boolean | null = null;

    @Input()
    pseudoPressed: boolean | null = null;

    @Input()
    pseudoFocused: boolean | null = null;

    /**
     * Determines if component is focusable with keyboard.
     */
    @Input()
    focusable = true;

    @Input()
    nativeId = '';

    @Output()
    readonly focusedChange = new EventEmitter<boolean>();

    @Output()
    readonly pressedChange = new EventEmitter<boolean>();

    @Output()
    readonly hoveredChange = new EventEmitter<boolean>();

    @Output()
    readonly focusVisibleChange = new EventEmitter<boolean>();

    @HostBinding('class._pressed')
    get computedPressed(): boolean {
        return this.pseudoPressed ?? this.pressed;
    }

    get computedFocusable(): boolean {
        return this.focusable || this.focused;
    }

    @HostBinding('class._focused')
    get computedFocused(): boolean {
        return this.pseudoFocused ?? this.focused;
    }

    @HostBinding('class._focus-visible')
    get computedFocusVisible(): boolean {
        return this.pseudoFocused ?? this.focusVisible;
    }

    @HostBinding('tabIndex')
    get tabIndex(): number {
        return this.focusable ? 0 : -1;
    }

    @HostListener('focusin', ['true'])
    @HostListener('focusout', ['false'])
    onFocused(focused: boolean) {
        this.updateFocused(focused);
    }


    @HostBinding('attr.data-state')
    get interactiveState(): InteractiveState | null {
        if (this.pressed) {
            return InteractiveState.Pressed;
        }

        if (this.hovered) {
            return InteractiveState.Hovered;
        }

        return null;
    }

    protected updatePressed(pressed: boolean) {
        if (this.pressed === pressed) {
            return;
        }

        this.pressed = pressed;
        this.pressedChange.emit(pressed);
    }

    protected updateFocused(focused: boolean) {
        this.focusedChange.emit(focused);
        this.focused = focused
    }

    protected updateFocusVisible(focusVisible: boolean) {
        if (this.focusVisible === focusVisible) {
            return;
        }

        this.focusVisible = focusVisible;
        this.focusVisibleChange.emit(focusVisible);
    }

    toggleElevation(elevated: boolean) {
        const element = this.elementRef.nativeElement as HTMLElement;
        if (elevated) {
            element.classList.add('icc-chip-elevated');
        } else {
            element.classList.remove('icc-chip-elevated');
        }
    }

    @ContentChildren(IconComponent, { read: ElementRef }) icons!: QueryList<ElementRef<HTMLElement>>;

    constructor(private elementRef: ElementRef) {}

    ngAfterViewInit() {
        if (this.icons?.length > 0) {
            if (this.icons.some(el => el.nativeElement.previousElementSibling == null)) {
                (this.elementRef.nativeElement as HTMLElement).classList.add('icc-chip-icon-before');
            }
            if (this.icons.some(el => el.nativeElement.nextElementSibling == null)) {
                (this.elementRef.nativeElement as HTMLElement).classList.add('icc-chip-icon-after');
            }
        }
    }
}

@Directive({
    selector: 'icc-chip[iccChipSmall]',
    host: {
        class: 'icc-chip-small',
    },
})
export class ChipSmallDirective {}

@Directive({
    selector: 'icc-chip[iccChipStatus]',
    host: {
        class: 'icc-chip-status',
    },

})
export class ChipStatusDirective implements OnChanges {
    @Input() color = '';

    @ContentChildren(IconComponent, { read: ElementRef }) icons!: QueryList<ElementRef<HTMLElement>>;

    constructor(private elementRef: ElementRef) {}

    ngOnChanges(): void {
        const colorObj = new TinyColor('#' + this.color);
        colorObj.setAlpha(0.3);
        this.elementRef.nativeElement.style.background = colorObj.toRgbString();

        const color2Obj = new TinyColor('#' + this.color);
        const colorTintObj = new TinyColor({ r: 0, g: 0, b: 0, a: .5});
        const textColor = colorTintObj.mix(color2Obj);
        this.elementRef.nativeElement.style.color = textColor.toRgbString();
    }

    ngAfterViewChecked() {
        if (this.icons?.length > 0) {
            this.icons.filter(el => el.nativeElement.previousElementSibling == null).forEach(el => {
                el.nativeElement.style.color = '#' + this.color
            });
        }
    }


}
